import { API_BASE } from './constants'

/**
 * Unified v2 error envelope (Plan-29 Stream O). Every /api/* route
 * that fails returns this shape; legacy /api/* routes return the bare
 * {detail: ...} shape. apiFetch parses both and exposes whichever
 * fields are present on the thrown ApiError.
 */
export interface ErrorEnvelope {
  error_id: string
  category: string
  recoverable: boolean
  user_message: string
  suggested_actions: Array<{ label: string; kind: string }>
  detail?: Record<string, unknown>
}

class ApiError extends Error {
  public logs?: string
  // FastAPI returns structured errors as `{detail: {...}}` — surface that
  // here so consumers can read fields like `balance_microusd` on a 402
  // out_of_credits response without a follow-up fetch.
  public detail?: Record<string, unknown>
  // Plan 15.4 G4 follow-up — preview routes set ``X-Fallback-Tried`` on
  // 5xx responses to surface which backends were attempted in the chain
  // (e.g. ``e2b,docker``). Empty/"none" means no fallback ran.
  public triedBackends?: string[]
  // Plan-29 Stream O — the full unified envelope (when present) + the
  // top-level fields the UI commonly reads without unwrapping. Absent on
  // legacy non-v2 error responses.
  public envelope?: ErrorEnvelope
  public errorId?: string
  public category?: string
  // X-Qyngent-Nudge header — single-word hint the UI uses to pick a
  // help-content key (e.g. "prompt_length_exceeded" → toast template).
  public nudge?: string
  // Plan 59 Ruling C2-35.5: a legacy body's machine-readable `code` (the copy is in `error`, which
  // becomes the message). Callers branch on this, never on the copy.
  public code?: string
  constructor(
    public status: number,
    message: string,
    logs?: string,
    detail?: Record<string, unknown>,
    triedBackends?: string[],
    envelope?: ErrorEnvelope,
    nudge?: string,
    code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.logs = logs
    this.detail = detail
    this.triedBackends = triedBackends
    this.envelope = envelope
    this.errorId = envelope?.error_id
    this.category = envelope?.category
    this.nudge = nudge
    this.code = code
  }
}

// Module-level flag: once a 401-on-authenticated request fires the
// session-expired signal, every subsequent in-flight 401 within the
// same page becomes a no-op. Otherwise a build-page mount with 20
// query hooks would dispatch 20 events, kick off 20 signOut() calls,
// and overwhelm the navigation. Reset by `qyngent:session-restored`
// (dispatched after sign-in / sign-up persists a fresh token) so the
// SAME page can recover without a hard reload.
let _sessionExpiredDispatched = false

if (typeof window !== 'undefined') {
  window.addEventListener('qyngent:session-restored', () => {
    _sessionExpiredDispatched = false
  })
}

export function getAuthToken(): string | null {
  // Plan 27 — prefer the qyngent-issued JWT (stored by lib/auth.ts).
  // During the cutover we still fall through to the Supabase
  // sb-<ref>-auth-token format so existing sessions keep working.
  try {
    const qyngentToken = localStorage.getItem('qyngent.access_token')
    if (qyngentToken) {
      return qyngentToken
    }
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const data = JSON.parse(localStorage.getItem(key) || '{}')
        return data?.access_token || null
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return null
}

/**
 * Active workspace id, read from the zustand `persist` blob rather than by
 * importing the store.
 *
 * `useWorkspaceStore` persists under the "qyngent-workspace" key as
 * `{"state":{"activeWorkspaceId":...},"version":0}` and writes synchronously
 * on every set, so this is always current. Importing the store here creates a
 * chunk cycle that breaks the production bundle — see the note in apiFetch.
 */
function getActiveWorkspaceId(): string | null {
  try {
    const raw = localStorage.getItem('qyngent-workspace')
    if (!raw) return null
    return JSON.parse(raw)?.state?.activeWorkspaceId ?? null
  } catch {
    return null
  }
}

/**
 * Active org id, read from the same persisted `persist` blob.
 *
 * The qyngent JWT carries no `org_id` claim (deliberately — see
 * dashboard/middleware/org_context.py), so the backend resolves the tenant
 * per request. Without this header a user who belongs to more than one org
 * always gets a default, which for an invited member is silently the wrong
 * tenant.
 */
function getActiveOrgId(): string | null {
  try {
    const raw = localStorage.getItem('qyngent-workspace')
    if (!raw) return null
    return JSON.parse(raw)?.state?.activeOrgId ?? null
  } catch {
    return null
  }
}

/**
 * Drop an org selection the server has told us is unusable.
 *
 * Deliberately narrower than clearStaleWorkspace: it nulls `activeOrgId`
 * inside the persisted blob rather than dropping the whole key, so an
 * unusable org selection doesn't also throw away a perfectly good workspace
 * selection. No store import here — see the cycle note in apiFetch.
 */
function clearStaleOrg(): void {
  try {
    const raw = localStorage.getItem('qyngent-workspace')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.state) {
        parsed.state.activeOrgId = null
        localStorage.setItem('qyngent-workspace', JSON.stringify(parsed))
      }
    }
  } catch {
    // Private mode or a corrupt blob — nothing safe to rewrite.
  }
  try {
    window.dispatchEvent(new CustomEvent('qyngent:org-reset'))
  } catch {
    // Non-browser context (SSR/tests without a window).
  }
}

/**
 * Drop a workspace selection the server has told us is unusable.
 *
 * The store is persisted, so it outlives the session: a browser carrying an
 * `activeWorkspaceId` from an earlier session (or another user) would keep
 * sending a header the backend can't honour. The backend falls back to the
 * caller's own default and sets X-Workspace-Reset; we clear the stored value
 * and announce it so the workspace store re-resolves. No store import here —
 * see the cycle note in apiFetch.
 */
function clearStaleWorkspace(): void {
  try {
    localStorage.removeItem('qyngent-workspace')
  } catch {
    // Private mode — nothing to clear.
  }
  try {
    window.dispatchEvent(new CustomEvent('qyngent:workspace-reset'))
  } catch {
    // Non-browser context (SSR/tests without a window).
  }
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  }

  // Attach auth token if available
  const token = getAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Stamp the active workspace so the backend scopes the request to what the
  // sidebar switcher shows. Without this, AuthMiddleware falls back to the
  // org's default workspace on every request and every workspace-scoped route
  // serves the wrong rows (fixed 2026-09-05).
  //
  // Read from localStorage, NOT by importing useWorkspaceStore. api.ts sits
  // near the root of the import graph, so importing the store put zustand's
  // `create` in a cycle with the entry chunk — the api chunk initialised
  // first and threw "n is not a function", blanking the whole app in prod.
  // getAuthToken() above reads localStorage for the same reason.
  // An explicit per-call header still wins.
  if (!headers['X-Workspace-Id']) {
    const activeWorkspaceId = getActiveWorkspaceId()
    if (activeWorkspaceId) {
      headers['X-Workspace-Id'] = activeWorkspaceId
    }
  }

  // Same contract for the tenant. The backend membership-verifies it and
  // falls back to the caller's own default when it can't be honoured.
  if (!headers['X-Org-Id']) {
    const activeOrgId = getActiveOrgId()
    if (activeOrgId) {
      headers['X-Org-Id'] = activeOrgId
    }
  }

  const res = await fetch(url, {
    // Always bypass browser disk cache for API calls so a stale prior
    // response (e.g. a SPA-fallback HTML cached during a misroute) can't
    // mask the real JSON. Caller can override via options.cache.
    cache: 'no-store',
    ...options,
    headers,
  })

  // The server could not honour the workspace we asked for and used our
  // default instead — drop the stale selection so we stop re-sending it.
  if (res.headers.get('X-Workspace-Reset')) {
    clearStaleWorkspace()
  }
  if (res.headers.get('X-Org-Reset')) {
    clearStaleOrg()
  }
  if (!res.ok) {
    // Session-expiry interceptor: a 401 on a request that DID carry a
    // Bearer token means the JWT was rejected (expired / malformed /
    // signing key rotated). Clear the dead token and broadcast a
    // session-expired event so the AppLayout can route the user to
    // /login with a banner. Skip when no token was sent — public
    // endpoints (anon-build, templates list) legitimately return 401
    // to unauthenticated callers and we shouldn't sign anyone out.
    if (res.status === 401 && token && !_sessionExpiredDispatched) {
      _sessionExpiredDispatched = true
      try {
        localStorage.removeItem('qyngent.access_token')
      } catch {
        /* localStorage blocked — fall through; the next call will 401 again */
      }
      try {
        window.dispatchEvent(new CustomEvent('qyngent:session-expired'))
      } catch {
        /* swallow — dispatchEvent shouldn't ever throw, but be safe */
      }
    }
    // Try to extract error detail from response body
    let errorMsg = `API ${res.status}: ${res.statusText}`
    let logs: string | undefined
    let detail: Record<string, unknown> | undefined
    let envelope: ErrorEnvelope | undefined
    let code: string | undefined
    try {
      const body = await res.json()
      // Plan-29 Stream O unified envelope first — recognised by the
      // full {error_id, category, recoverable, user_message,
      // suggested_actions} shape. A partial envelope (just error_id +
      // category) is rejected — keeps legacy fallback semantics.
      if (
        body &&
        typeof body === 'object' &&
        typeof body.error_id === 'string' &&
        typeof body.category === 'string' &&
        typeof body.recoverable === 'boolean' &&
        typeof body.user_message === 'string' &&
        Array.isArray(body.suggested_actions)
      ) {
        envelope = body as ErrorEnvelope
        errorMsg = envelope.user_message || errorMsg
        if (envelope.detail && typeof envelope.detail === 'object') {
          detail = envelope.detail as Record<string, unknown>
        }
      } else {
        // Legacy FastAPI shape: {error, logs, detail}
        if (body.error) errorMsg = body.error
        if (typeof body.code === 'string') code = body.code
        if (body.logs) logs = body.logs
        if (body.detail && typeof body.detail === 'object') {
          detail = body.detail as Record<string, unknown>
          if (typeof detail.message === 'string') errorMsg = detail.message
        } else if (typeof body.detail === 'string') {
          errorMsg = body.detail
        }
      }
    } catch {
      // Response body not JSON — use default message
    }
    // X-Fallback-Tried is set by preview routes; absent / "none" means
    // no fallback was attempted.
    const triedHeader = res.headers.get('X-Fallback-Tried')
    const triedBackends =
      triedHeader && triedHeader !== 'none'
        ? triedHeader.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined
    // Plan-29 Stream O — single-word UI hint for help-content lookup.
    const nudge = res.headers.get('X-Qyngent-Nudge') || undefined
    throw new ApiError(res.status, errorMsg, logs, detail, triedBackends, envelope, nudge, code)
  }
  // 204 No Content (and other empty bodies, e.g. DELETE endpoints) have nothing
  // to parse — res.json() would throw "Unexpected end of JSON input" on the empty
  // body and turn a successful mutation into a spurious error. Return undefined.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T
  }
  return res.json()
}

/**
 * Download a binary endpoint (e.g. a .zip export) as a file. Unlike a plain <a href>, this
 * attaches the Bearer token (auth is a token, not a cookie), fetches the blob, and triggers a
 * save via an object URL. Throws ApiError on non-2xx.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const token = getAuthToken()
  const res = await fetch(`${API_BASE}${path}`, {
    cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export { ApiError }
